/* Рамка одного фото или ролика «Промпта из фото / видео» (§7.3, BotCore.upload вида photo / video): пусто — «Загрузите фото»,
   с файлом — снимок в пропорциях на размытом фоне (ролик — первый кадр и длительность), «Заменить · Убрать».
   Длинный ролик — фрагмент под рамкой, только если форма публикует поле начала фрагмента. */
import { useId, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';
import type { ToolField } from '../../studio/circle/tool-form';
import { StudioIcon } from '../../studio/StudioIcon';
import { useVideoSourcePreview } from '../../video/use-video-source-preview';
import { shake } from '../../../ui/motion';
import { limitsNote, mmss } from './text-model';
import { readSourceMeta, type SourceMeta } from './text-media';
import { FrameActs, FrameEmpty, RightsCap } from './TextFrameParts';
import { useConsentUpload } from './use-consent-upload';

export function TextMediaFrame({
  state,
  fields,
  meta,
  setMeta,
  fromKey,
  clipMaxMs,
}: {
  state: CircleFormState;
  /** Фото и (если политика включает «Промпт из видео») поле ролика: рамка принимает одно из них */
  fields: ToolField[];
  meta: Record<string, SourceMeta>;
  setMeta(key: string, value: SourceMeta | null): void;
  fromKey?: string;
  clipMaxMs: number;
}) {
  const inputId = useId();
  const frame = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const consent = useConsentUpload(state);
  const active = fields.find(field => state.inputs[field.key] || state.uploadIds[field.key]);
  const photoField = fields.find(field => field.upload?.kind === 'photo');
  const videoField = fields.find(field => field.upload?.kind === 'video');
  const photoPreview = useVideoSourcePreview(
    photoField ? state.inputs[photoField.key] : undefined,
    state.ownerId,
    'photo',
    photoField?.upload?.max_bytes ?? 10485760,
  );
  const info = active ? meta[active.key] : undefined;
  const disabled = state.locked;
  const fail = (text: string) => {
    setError(text);
    shake(frame.current);
  };
  async function select(file: File) {
    setError('');
    const field = fields.find(item => item.upload?.accept.includes(file.type));
    if (!field?.upload) return fail(uiFormat('Этот формат не подходит: {0}', limitsNote(fields)));
    if (field.upload.max_bytes && file.size > field.upload.max_bytes)
      return fail(uiFormat('Файл больше {0} МБ', Math.round(field.upload.max_bytes / 1048576)));
    const kind = field.upload.kind === 'video' ? 'video' : 'photo';
    const read = kind === 'video' ? await readSourceMeta(file, 'video') : { name: file.name, size: file.size, kind };
    consent.take(file, async (chosen, current) => {
      for (const other of fields) if (other.key !== field.key && (current.inputs[other.key] || current.uploadIds[other.key])) current.clearUpload(other.key);
      if (kind === 'photo') photoPreview.select(chosen);
      setMeta(field.key, read as SourceMeta);
      if (fromKey) current.setInputs(value => ({ ...value, [fromKey]: '0' }));
      if (!(await current.upload(field.key, chosen))) setMeta(field.key, null);
    });
  }
  const remove = () => {
    if (!active) return;
    state.clearUpload(active.key);
    setMeta(active.key, null);
    if (active.upload?.kind === 'photo') photoPreview.select(null);
  };
  const picture = active?.upload?.kind === 'photo' ? photoPreview.url : info?.poster;
  const duration = info?.durationMs ?? 0;
  const from = fromKey ? Number(state.inputs[fromKey] || 0) : 0;
  const longClip = active?.upload?.kind === 'video' && !!fromKey && duration > clipMaxMs;
  const pending = active && state.uploadIds[active.key] && !state.inputs[active.key];
  return (
    <div className="pf-up tt-up">
      <div ref={frame} className={`bcu${active ? ' has' : ''}`} id={videoField && !photoField ? 'ttVdUp' : 'ttPhUp'}>
        <input
          id={inputId}
          type="file"
          hidden
          accept={fields.flatMap(field => field.upload?.accept ?? []).join(',')}
          disabled={disabled}
          aria-label={uiText(videoField && !photoField ? 'Видео' : videoField ? 'Фото или видео' : 'Фото')}
          onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void select(file);
          }}
        />
        <div className="bcu-st">
          {!active ? (
            <FrameEmpty
              inputId={inputId}
              icon={videoField && !photoField ? 'video' : 'image'}
              title={videoField && !photoField ? 'Загрузите видео' : videoField ? 'Загрузите фото или видео' : 'Загрузите фото'}
              sub={videoField && !photoField ? 'Опишем сцену, движение камеры и свет' : 'Опишем снимок так, чтобы его можно было повторить'}
              note={error || limitsNote(fields)}
              bad={!!error}
              disabled={disabled}
            />
          ) : (
            <div className="bcu-f dk">
              {picture && <img className="bcu-bg" src={picture} alt="" aria-hidden="true" />}
              <div className="bcu-pa">
                <span className="bcu-pv" style={info?.ratio ? { aspectRatio: String(info.ratio) } : undefined}>
                  {picture ? (
                    <img src={picture} alt={uiText(active.upload?.kind === 'video' ? 'Первый кадр' : 'Исходное фото')} />
                  ) : (
                    <span className="bcu-sqd">
                      <StudioIcon kind={active.upload?.kind === 'video' ? 'video' : 'image'} />
                      <small>{uiText(photoPreview.loading ? 'Загружаем…' : state.inputs[active.key] ? 'Загружено' : 'Проверяем файл…')}</small>
                    </span>
                  )}
                  {active.upload?.kind === 'video' && !!duration && (
                    <span className="bcu-dur">
                      <StudioIcon kind="play" />
                      <b className="mono">{mmss(longClip ? clipMaxMs : duration)}</b>
                    </span>
                  )}
                </span>
              </div>
              <FrameActs inputId={inputId} disabled={disabled} onRemove={remove} />
            </div>
          )}
        </div>
      </div>
      {active && error && (
        <p className="bcu-n bad" role="alert">
          {uiText(error)}
        </p>
      )}
      {photoPreview.error && active?.upload?.kind === 'photo' && (
        <p role="alert">
          {uiText('Не удалось открыть фото.')}{' '}
          <button type="button" className="lnk" onClick={photoPreview.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {pending && (
        <button type="button" className="lnk" disabled={state.busy} onClick={() => void state.upload(active!.key, null)}>
          {uiText('Продолжить проверку')}
        </button>
      )}
      {longClip && fromKey && (
        <label className="pf-trw text-trim">
          <span>{uiFormat('Фрагмент · {0} из {1}', mmss(from), mmss(duration))}</span>
          <input
            type="range"
            min={0}
            max={Math.max(0, duration - clipMaxMs)}
            step={500}
            value={from}
            disabled={disabled}
            onChange={event => state.setInputs(value => ({ ...value, [fromKey]: event.target.value }))}
          />
          <small>{uiFormat('максимум {0} секунд', Math.round(clipMaxMs / 1000))}</small>
        </label>
      )}
      <RightsCap />
      {consent.sheet}
    </div>
  );
}
